/**
 * InlineChatSuggestions Component
 * Displays AI-powered autocomplete suggestions as an overlay attached to the
 * chat input. Positioned absolutely so it does not push the input or other
 * content — it overlays adjacent content.
 *
 * Supports two directions via the `direction` prop:
 * - `'down'` (default) — overlay extends below the input (out-of-session homepage)
 * - `'up'` — overlay extends above the input (in-session sidebar)
 *
 * - Shares visual border with the input
 * - Pro suggestions show with tier-pro styling
 * - Clicking a suggestion submits the message directly
 * - Once open it stays open while the input is focused, showing a skeleton in place of
 *   the rows whenever a newly typed query is still loading (see suggestion-sheet-state)
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { contextLinkingPopoverAtom } from '@/atoms/chatAtoms';
import {
  SuggestionSkeletonRows,
  useSuggestionSheetState,
} from '@/components/chat/components/suggestions/suggestion-sheet-state';
import { useChatSuggestions } from '@/components/chat/hooks/useChatSuggestions';
import type { ChatSuggestion } from '@/components/chat/types/types';
import { TypographyFootNote } from '@heidi/design-system/typography';
import { useIsMobile } from '@heidi/utils';
import { cn } from '@/lib/cn';
import type { Editor } from '@tiptap/react';
import { useAtomValue } from 'jotai';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useState } from 'react';

interface InlineChatSuggestionsProps {
  editor: Editor | null;
  disabled?: boolean;
  onSend: (message: string) => void;
  /** Called when suggestion visibility changes — lets parent coordinate border-radius */
  onVisibilityChange?: (visible: boolean) => void;
  /** When set, triggers an immediate (non-debounced) suggestion search for this text.
   *  Use after programmatic input changes like verb suggestion populate. */
  forceSearchText?: string | null;
  /** Fired once `forceSearchText` has been searched so a host can clear and re-arm it —
   *  populating the same starter twice is otherwise a no-op (the effect is keyed on the string). */
  onForceSearchConsumed?: () => void;
  /** When true, renders as a static full-view panel (mobile active state)
   *  instead of an absolute dropdown overlay. Fills available flex space. */
  fullView?: boolean;
  /** Direction the overlay extends from the input.
   *  - `'down'` (default): below the input (out-of-session homepage)
   *  - `'up'`: above the input (in-session sidebar) */
  direction?: 'down' | 'up';
  /** Row type size. Defaults to `fullView`, which is how mobile got its larger rows.
   *  A host whose composer runs at `text-base` sets it directly so the sheet does not
   *  read a size below the input it hangs off. */
  largeText?: boolean;
  /** Outer corner radius, matched to the host composer it seams against.
   *  Defaults to `xl` for Evidence; Work home runs a `2xl` composer. */
  radius?: 'xl' | '2xl';
  /** External "another menu owns the input" signal, OR-ed with the `contextLinkingPopoverAtom`
   *  read below. Evidence's composer is the only writer of that atom; a host with its own
   *  `@` / `/` picker (Work home) reports it here instead, so both surfaces honour the
   *  one-menu-at-a-time rule without a second copy of this component. */
  suppressed?: boolean;
}

interface SuggestionItemProps {
  suggestion: ChatSuggestion;
  onClick: () => void;
  index: number;
  inputText: string;
  /** Use larger text for mobile full-view mode */
  largeText?: boolean;
}

/**
 * Renders suggestion text with the matching prefix in tertiary colour
 * and the novel completion portion in primary colour.
 */
const HighlightedSuggestionText = ({
  content,
  inputText,
}: {
  content: string;
  inputText: string;
}) => {
  const lowerContent = content.toLowerCase();
  const lowerInput = inputText.toLowerCase().trim();

  // If the suggestion starts with the input text, split at the boundary
  if (lowerInput && lowerContent.startsWith(lowerInput)) {
    const matchedPortion = content.slice(0, lowerInput.length);
    const completionPortion = content.slice(lowerInput.length);

    return (
      <>
        <span className="text-text-tertiary">{matchedPortion}</span>
        <span className="text-text-primary">{completionPortion}</span>
      </>
    );
  }

  // No prefix match — render entirely in primary
  return <span className="text-text-primary">{content}</span>;
};

const SuggestionItem = ({ suggestion, onClick, index, inputText, largeText = false }: SuggestionItemProps) => {
  const isPro = suggestion.type === 'pro';

  return (
    <m.button
      type="button"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.15,
        delay: index * 0.03,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 rounded-xl px-4 py-3 text-left transition-colors duration-150 ease-in-out',
        largeText ? 'text-base leading-normal' : 'text-sm leading-5',
        'hover:bg-surface-hover',
        isPro && 'text-tier-pro-text'
      )}
    >
      <span className="min-w-0 flex-1 break-words">
        {isPro ? (
          suggestion.content
        ) : (
          <HighlightedSuggestionText content={suggestion.content} inputText={inputText} />
        )}
      </span>
      {isPro && (
        <span className="flex shrink-0 items-center gap-0.5 opacity-80">
          <TierProFilled className="size-2.5 text-tier-pro" />
          <TypographyFootNote>Pro</TypographyFootNote>
        </span>
      )}
    </m.button>
  );
};

export const InlineChatSuggestions = ({
  editor,
  disabled = false,
  onSend,
  onVisibilityChange,
  forceSearchText,
  onForceSearchConsumed,
  fullView = false,
  direction = 'down',
  largeText = fullView,
  radius = 'xl',
  suppressed = false,
}: InlineChatSuggestionsProps) => {
  const {
    suggestions,
    handleSuggestionClick,
    shouldShowSuggestions,
    searchText,
    searchImmediately,
    isSuggestionsPending,
    isSuggestionsRefreshing,
  } = useChatSuggestions({ editor, enabled: !disabled, onSend });

  // Hide inline suggestions when a context-linking popover (@ patient / # session) is active
  const isContextPickerActive = useAtomValue(contextLinkingPopoverAtom) !== null || suppressed;

  // Track editor focus so suggestions hide when the input is inactive
  const [isEditorFocused, setIsEditorFocused] = useState(() => editor?.isFocused ?? false);

  useEffect(() => {
    if (!editor) return;

    // Sync initial state in case editor focus changed before effect ran
    setIsEditorFocused(editor.isFocused);

    const handleFocus = () => setIsEditorFocused(true);
    const handleBlur = () => setIsEditorFocused(false);

    editor.on('focus', handleFocus);
    editor.on('blur', handleBlur);

    return () => {
      editor.off('focus', handleFocus);
      editor.off('blur', handleBlur);
    };
  }, [editor]);

  const showSuggestions = shouldShowSuggestions && suggestions.length > 0;

  // Holds the sheet open while the next set of suggestions loads, rather than
  // closing on the keystroke and reopening on the response.
  const { hasContent, showSkeletonRows, skeletonRowCount } = useSuggestionSheetState({
    hasSuggestions: showSuggestions,
    suggestionCount: suggestions.length,
    isSuggestionsRefreshing,
    isSuggestionsPending,
    forceSearchText,
    searchText,
  });

  const isMobile = useIsMobile();

  // Gate panel visibility on editor focus and context picker state:
  // - hide when user clicks away (blur)
  // - hide when @ patient or # session picker is active (avoid two menus)
  const showPanel = hasContent && isEditorFocused && !isContextPickerActive;

  // Notify parent when panel visibility changes so it can coordinate border-radius
  useEffect(() => {
    onVisibilityChange?.(showPanel);
  }, [showPanel, onVisibilityChange]);

  // Trigger an immediate search when forceSearchText is set (e.g. after verb suggestion populate)
  useEffect(() => {
    if (forceSearchText) {
      searchImmediately(forceSearchText);
      onForceSearchConsumed?.();
    }
  }, [forceSearchText, onForceSearchConsumed, searchImmediately]);

  const suggestionContent = showSkeletonRows ? (
    <SuggestionSkeletonRows count={skeletonRowCount} largeText={largeText} />
  ) : (
    suggestions.map((suggestion, index) => (
      <SuggestionItem
        key={`${suggestion.content?.slice(0, 20) ?? ''}-${index}`}
        suggestion={suggestion}
        onClick={() => handleSuggestionClick(index)}
        index={index}
        inputText={searchText}
        largeText={largeText}
      />
    ))
  );

  // Full-view mode: always-mounted flex-1 spacer so the input stays at the bottom.
  // Suggestion content fades in inside it — no layout shift when results arrive.
  if (fullView && !isMobile) {
    return (
      <div
        className="flex-1 overflow-y-auto py-2.5"
        onMouseDown={(e) => e.preventDefault()}
      >
        <AnimatePresence>
          {showPanel && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {suggestionContent}
            </m.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const isUp = direction === 'up';
  const sheetCornerRadiusClassName =
    radius === '2xl'
      ? isUp
        ? 'rounded-t-2xl'
        : 'rounded-b-2xl'
      : isUp
        ? 'rounded-t-xl'
        : 'rounded-b-xl';

  // Default: absolutely positioned overlay
  // 'down' — sits below the input, covers content beneath
  // 'up'   — sits above the input, covers content above (in-session sidebar)
  return (
    <AnimatePresence>
      {showPanel && (
        <m.div
          initial={{ opacity: 0, y: isUp ? 4 : -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: isUp ? 4 : -4 }}
          transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          // Prevent mousedown from stealing focus away from the editor,
          // so clicking a suggestion does not blur the input first.
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            'absolute inset-x-0 z-10 overflow-hidden border border-border bg-surface px-2 py-2.5 shadow-sm transition-colors duration-200 ease-in-out group-focus-within/chatinput:border-border-pressed',
            isUp
              ? cn('bottom-full -mb-px border-b-border/30', sheetCornerRadiusClassName)
              : cn('top-full -mt-px', sheetCornerRadiusClassName)
          )}
        >
          {suggestionContent}
        </m.div>
      )}
    </AnimatePresence>
  );
};
